import React, { useMemo, useState } from 'react';

import { PaginationOptionsChangePayload } from '../types';
import { RowsPerPageOption } from '../../table/types';

import Input from '../../../components/input';
import Select from '../../../components/select';
import Pagination from '../../../components/pagination';
import { Placement } from '../../../components/overlay';

import { getRowsPerPageOptions } from '../../table/utils/get-rows-per-page-options';
import { formatNumberWithCommas } from '../../../../utils/format-number-with-commas';

type IProps = {
  page: number;
  limit: number;
  totalRows: number;
  customOptions: number[];
  onPaginationOptionsChange: (payload: PaginationOptionsChangePayload) => void;
  disableGoToPage?: boolean;
  maxPaginationCount?: number;
  maxAllowedPage?: number;
  paginationRestrictionTooltip?: string;
};

const TablePagination: React.FC<IProps> = ({
  page,
  limit,
  totalRows,
  onPaginationOptionsChange,
  customOptions = [],
  disableGoToPage = false,
  maxPaginationCount,
  maxAllowedPage,
  paginationRestrictionTooltip,
}) => {
  const [goToPage, setGoToPage] = useState(1);

  const rowsPerPageOptions = useMemo(
    () => getRowsPerPageOptions(customOptions),
    [customOptions],
  );

  const totalPages = useMemo(() => {
    // If maxPaginationCount is set, use it to limit the number of pages
    // but keep the original totalRows for display purposes
    const effectiveTotalRows = maxPaginationCount
      ? Math.min(totalRows, maxPaginationCount)
      : totalRows;
    
    const totalPagesCount = Math.ceil(effectiveTotalRows / limit);

    if (page > totalPagesCount) {
      onPaginationOptionsChange({ page: 1, limit });
    }

    return totalPagesCount;
  }, [totalRows, limit, maxPaginationCount]);

  const onGoToPageValueChange = (value: string) => {
    setGoToPage(Number(value));
  };

  const handleGoToPage = (e) => {
    e.preventDefault();
    const targetPage = maxAllowedPage
      ? Math.min(goToPage, maxAllowedPage)
      : goToPage;
    onPaginationOptionsChange({ page: targetPage, limit });
  };

  useMemo(() => {
    setGoToPage(page);
  }, [page]);

  const start = formatNumberWithCommas((page - 1) * limit + 1);
  const end = formatNumberWithCommas(
    page * limit > totalRows ? totalRows : page * limit,
  );

  return (
    <div className="tanstack-table--pagination">
      <div className="tanstack-table--pagination__container">
        <p className="tanstack-table--pagination__text">Showing</p>
        {rowsPerPageOptions?.length > 1 ? (
          <>
            <Select<RowsPerPageOption>
              className="tanstack-table--pagination__select"
              options={rowsPerPageOptions}
              selectedOptionKey={limit.toString()}
              selectedOptionRenderer={([option]) => (
                <span>
                  {option?.value > totalRows ? totalRows : option?.value}
                </span>
              )}
              optionRenderer={(option) => <span>{option?.value}</span>}
              onChange={([option]) =>
                onPaginationOptionsChange({ page, limit: option.value })
              }
              placeholder="Select Limit"
              placement={Placement.Top}
            />
            <p className="tanstack-table--pagination__text">{`out of ${totalRows}`}</p>
          </>
        ) : (
          <p className="tanstack-table--pagination__text">
            {`${start}-${end} of ${formatNumberWithCommas(totalRows)}`}
          </p>
        )}
      </div>

      <div className="tanstack-table--pagination__separator" />

      {!disableGoToPage && (
        <div className="tanstack-table--pagination__container">
          <p className="tanstack-table--pagination__text">Go to page:</p>

          <div className="tanstack-table--pagination__input">
            <form onSubmit={handleGoToPage}>
              <Input
                type="number"
                value={goToPage}
                onChange={onGoToPageValueChange}
                min={1}
                max={maxAllowedPage ? Math.min(totalPages, maxAllowedPage) : totalPages}
              />
            </form>
          </div>
          <p className="tanstack-table--pagination__text">{`/${totalPages}`}</p>
        </div>
      )}

      {page <= totalPages && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          maxAllowedPage={maxAllowedPage}
          restrictionTooltip={paginationRestrictionTooltip}
          onChange={(newPage) => {
            onPaginationOptionsChange({ page: newPage, limit });
          }}
        />
      )}
    </div>
  );
};

export default TablePagination;
